import { AlertCircleIcon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export interface FormFieldProps {
  label: React.ReactNode;
  /** Helper text shown below the label. Linked with aria-describedby. */
  description?: React.ReactNode;
  /** Validation message. Sets aria-invalid and links the message with aria-describedby. */
  error?: React.ReactNode;
  /** Marks the control as required (aria-required + visible "obligatorio" cue). */
  required?: boolean;
  /** Hide the label visually (still available to screen readers). */
  hideLabel?: boolean;
  className?: string;
  /** A single form control element; id and aria props are injected. */
  children: React.ReactElement<Record<string, unknown>>;
  id?: string;
}

/**
 * Label + description + error wrapper. Injects `id`, `aria-describedby`, `aria-invalid` and
 * `aria-required` into its single child control.
 */
export function FormField({ label, description, error, required, hideLabel, className, children, id }: FormFieldProps) {
  const autoId = React.useId();
  const controlId = id ?? (children.props['id'] as string | undefined) ?? `ef-field-${autoId}`;
  const descId = description ? `${controlId}-description` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const existing = children.props['aria-describedby'] as string | undefined;
  const describedBy = [existing, descId, errorId].filter(Boolean).join(' ') || undefined;
  const control = React.cloneElement(children, {
    id: controlId,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : children.props['aria-invalid'],
    'aria-required': required || children.props['aria-required'],
  });
  return (
    <div className={cn('grid gap-1.5', className)}>
      <label htmlFor={controlId} className={cn('text-body font-medium text-text-primary', hideLabel && 'sr-only')}>
        {label}
        {required ? (
          <span className="ml-1 font-normal text-text-secondary">
            <span aria-hidden>*</span>
            <span className="sr-only">(obligatorio)</span>
          </span>
        ) : null}
      </label>
      {description ? (
        <p id={descId} className="-mt-0.5 text-meta text-text-secondary">
          {description}
        </p>
      ) : null}
      {control}
      {error ? (
        <p id={errorId} className="flex items-center gap-1.5 text-meta font-medium text-danger">
          <Icon icon={AlertCircleIcon} aria-hidden className="size-4 shrink-0" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface FieldsetProps extends React.FieldsetHTMLAttributes<HTMLFieldSetElement> {
  legend: React.ReactNode;
  description?: React.ReactNode;
}

/** Groups related controls (e.g. a radio group or date + time) under a legend. */
export function Fieldset({ legend, description, className, children, ...props }: FieldsetProps) {
  return (
    <fieldset className={cn('grid min-w-0 gap-3', className)} {...props}>
      <legend className="mb-1 text-body font-medium text-text-primary">{legend}</legend>
      {description ? <p className="-mt-2 text-meta text-text-secondary">{description}</p> : null}
      {children}
    </fieldset>
  );
}
